/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState } from "react";
import {
  Grid,
  IconButton,
  CircularProgress,
  Modal,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { OrderContext } from "../../Contexts/OrderContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { urlEnum } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { deleteData } from "../../Services/deleteData";
import DropzoneComponent from "../GenericComponents/DropzoneComponent";
import styles from "../../Styles/showImages.module.css";
import { toast } from "react-toastify";
import axios from "axios";
import { disableTostify } from "../../Utils/Constants";

type PhotoCategory = "client" | "before" | "after";

type PhotoZoneProps = {
  title: string;
  photos: string[];
  category: PhotoCategory;
  imageUrls: Map<string, string>;
  loading: boolean;
  orderId: string;
  onUpload: (files: any[], category: PhotoCategory) => void;
  onDelete: (photo: string, category: PhotoCategory) => void;
  onOpenModal: (photos: string[], index: number) => void;
};

const categoryColors: Record<PhotoCategory, { border: string; bg: string; chip: string }> = {
  client: { border: "#1976d2", bg: "#e3f2fd", chip: "#1976d2" },
  before:  { border: "#ed6c02", bg: "#fff3e0", chip: "#ed6c02" },
  after:   { border: "#2e7d32", bg: "#e8f5e9", chip: "#2e7d32" },
};

function PhotoZone({
  title,
  photos,
  category,
  imageUrls,
  loading,
  orderId,
  onUpload,
  onDelete,
  onOpenModal,
}: PhotoZoneProps) {
  const colors = categoryColors[category];
  return (
    <div
      style={{
        marginBottom: 16,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          backgroundColor: colors.bg,
          borderBottom: `1px solid ${colors.border}`,
          padding: "6px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: colors.chip,
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle2" style={{ fontWeight: "bold", color: colors.chip }}>
          {title}
        </Typography>
        {photos && photos.length > 0 && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              color: colors.chip,
              fontWeight: 600,
              backgroundColor: "rgba(0,0,0,0.07)",
              borderRadius: 12,
              padding: "1px 8px",
            }}
          >
            {photos.length}
          </span>
        )}
      </div>
      <div style={{ padding: "10px 12px" }}>
      {orderId && (
        <div style={{ marginBottom: 10 }}>
          <DropzoneComponent
            onSave={(files: any[]) => onUpload(files, category)}
            maxFiles={10}
            hidePreview
          />
        </div>
      )}
      {photos && photos.length > 0 && (
        <Grid container spacing={1}>
          {photos.map((photo: string, index: number) => (
            <Grid item xs={6} sm={4} md={3} key={`${category}-${index}`}>
              <div
                className={styles.displayImagesSmall}
                style={{ position: "relative" }}
              >
                <IconButton
                  className={styles.deleteImagesBtn}
                  onClick={() => onDelete(photo, category)}
                  disabled={loading}
                  size="small"
                  style={{ backgroundColor: "rgba(255,255,255,0.8)" }}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
                {imageUrls.get(photo) ? (
                  <img
                    src={imageUrls.get(photo)}
                    alt={`${title} ${index + 1}`}
                    onClick={() => onOpenModal(photos, index)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            </Grid>
          ))}
        </Grid>
      )}
      </div>
    </div>
  );
}

export default function OrderPhotos() {
  const orderContext = useContext(OrderContext);
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPhotos, setModalPhotos] = useState<string[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const token = localStorage.getItem("access_token");
  const axiosConfig: any = token
    ? {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "cache-control": "no-cache",
        },
      }
    : {};

  const allPhotos = [
    ...(orderContext.order?.clientPhotos || []),
    ...(orderContext.order?.servicePhotosBefore || []),
    ...(orderContext.order?.servicePhotosAfter || []),
  ];

  useEffect(() => {
    allPhotos.forEach(async (photo: string) => {
      if (imageUrls.has(photo)) return;
      try {
        const res = await axios.get(
          `${urlEnum.products}/getImage/productsImages/${encodeURIComponent(photo)}`,
          axiosConfig,
        );
        const url = URL.createObjectURL(res.data);
        setImageUrls((prev) => new Map(prev).set(photo, url));
      } catch {
        // ignore fetch errors
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderContext.order?.clientPhotos,
    orderContext.order?.servicePhotosBefore,
    orderContext.order?.servicePhotosAfter,
  ]);

  async function uploadPhotos(filesToUpload: any[], category: PhotoCategory) {
    if (!orderContext.order?._id || filesToUpload.length === 0) return;

    setLoading(true);
    const formData = new FormData();
    filesToUpload.forEach((file: any) => {
      formData.append("files", file, file.name);
    });

    const fieldMap: Record<PhotoCategory, string> = {
      client: "clientPhotos",
      before: "servicePhotosBefore",
      after: "servicePhotosAfter",
    };
    const existing = (orderContext.order as any)[fieldMap[category]] || [];
    formData.append("photos", JSON.stringify(existing));
    formData.append("category", category);

    try {
      const res = await postData(
        `${urlEnum.orders}/photos/${orderContext.order._id}${disableTostify}`,
        formData,
      );
      if (res && res.data.result) {
        orderContext.setOrder(res.data.result);
        toast.success(Vocabulary.updateDataSuccess);
      }
    } catch {
      toast.error(Vocabulary.updateDataFailed);
    } finally {
      setLoading(false);
    }
  }

  async function deletePhoto(photoName: string, category: PhotoCategory) {
    if (!orderContext.order?._id) return;

    setLoading(true);
    try {
      const res = await deleteData(
        `${urlEnum.orders}/photos/${orderContext.order._id}/${encodeURIComponent(photoName)}?category=${category}`,
      );
      if (res && res.data.result) {
        const blobUrl = imageUrls.get(photoName);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setImageUrls((prev) => {
          const next = new Map(prev);
          next.delete(photoName);
          return next;
        });
        orderContext.setOrder(res.data.result);
        toast.success(Vocabulary.deleteDataSuccess);
      }
    } catch {
      toast.error(Vocabulary.deleteDataFailed);
    } finally {
      setLoading(false);
    }
  }

  function openPhotoModal(photos: string[], index: number) {
    setModalPhotos(photos);
    setCurrentPhotoIndex(index);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  function goToPreviousPhoto() {
    setCurrentPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : modalPhotos.length - 1,
    );
  }

  function goToNextPhoto() {
    setCurrentPhotoIndex((prev) =>
      prev < modalPhotos.length - 1 ? prev + 1 : 0,
    );
  }

  useEffect(() => {
    if (!modalOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeModal();
      else if (event.key === "ArrowLeft") goToPreviousPhoto();
      else if (event.key === "ArrowRight") goToNextPhoto();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, modalPhotos]);

  const orderId = orderContext.order?._id || "";

  return (
    <div>
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 10 }}>
          <CircularProgress size={24} />
        </div>
      )}

      {/* Client photos */}
      <PhotoZone
        title={Vocabulary.clientPhotos}
        photos={orderContext.order?.clientPhotos || []}
        category="client"
        imageUrls={imageUrls}
        loading={loading}
        orderId={orderId}
        onUpload={uploadPhotos}
        onDelete={deletePhoto}
        onOpenModal={openPhotoModal}
      />

      {/* Service photos group */}
      <div
        style={{
          border: "1px solid #bdbdbd",
          borderRadius: 8,
          overflow: "hidden",
          marginTop: 8,
        }}
      >
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #bdbdbd",
            padding: "6px 12px",
          }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#424242" }}>
            {Vocabulary.servicePhotos}
          </Typography>
        </div>
        <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
          <PhotoZone
            title={Vocabulary.photosBefore}
            photos={orderContext.order?.servicePhotosBefore || []}
            category="before"
            imageUrls={imageUrls}
            loading={loading}
            orderId={orderId}
            onUpload={uploadPhotos}
            onDelete={deletePhoto}
            onOpenModal={openPhotoModal}
          />

          <PhotoZone
            title={Vocabulary.photosAfter}
            photos={orderContext.order?.servicePhotosAfter || []}
            category="after"
            imageUrls={imageUrls}
            loading={loading}
            orderId={orderId}
            onUpload={uploadPhotos}
            onDelete={deletePhoto}
            onOpenModal={openPhotoModal}
          />
        </div>
      </div>

      {/* Photo viewer modal */}
      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderBottom: "1px solid #ddd",
            }}
          >
            <Box sx={{ fontSize: 14, color: "text.secondary" }}>
              {currentPhotoIndex + 1} / {modalPhotos.length}
            </Box>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              bgcolor: "#000",
              overflow: "hidden",
            }}
          >
            {modalPhotos[currentPhotoIndex] &&
            imageUrls.get(modalPhotos[currentPhotoIndex]) ? (
              <img
                src={imageUrls.get(modalPhotos[currentPhotoIndex])}
                alt={`Photo ${currentPhotoIndex + 1}`}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <CircularProgress />
            )}

            {modalPhotos.length > 1 && (
              <>
                <IconButton
                  onClick={goToPreviousPhoto}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.8)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton
                  onClick={goToNextPhoto}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.8)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
